import productsData from './latestProducts';

// Transform the new nested JSON structure into a flat array for the app
export const transformProducts = () => {
  const products = [];
  
  Object.entries(productsData).forEach(([manufacturerKey, manufacturerData]) => {
    const manufacturerName = manufacturerData.manufacturer || manufacturerKey;
    const productsList = manufacturerData.products || [];
    
    productsList.forEach((product) => {
      const specs = product.specs || {};
      const concerns = product.concerns || {};
      const features = product.important_features || {};
      
      // Skip products with no specs
      if (!specs || Object.keys(specs).length === 0) {
        return;
      }
      
      // Extract CADR - use smoke as primary, fallback to dust or pollen
      const cadrSmoke = typeof specs["CADR (Smoke)"] === 'string' 
        ? parseInt(specs["CADR (Smoke)"].replace(/[^\d]/g, '') || '0')
        : specs["CADR (Smoke)"] || 0;
      const cadrDust = typeof specs["CADR (Dust)"] === 'string'
        ? parseInt(specs["CADR (Dust)"].replace(/[^\d]/g, '') || '0')
        : specs["CADR (Dust)"] || 0;
      const cadr = cadrSmoke || cadrDust || 0;
      
      // Extract coverage (room size) - now should be a number
      const coverage = typeof specs.Coverage === 'number' 
        ? specs.Coverage 
        : parseInt(specs.Coverage || '0');
      
      // Extract price - now should be a number
      const price = typeof specs["Price (without discounts)"] === 'number'
        ? specs["Price (without discounts)"]
        : parseInt(specs["Price (without discounts)"] || '0');
      
      // Extract energy consumption (watts)
      const energyText = specs["Energy Consumption"] || '';
      const energyMatch = typeof energyText === 'string' ? energyText.match(/(\d+(?:\.\d+)?)/g) : null;
      let energyUsage = 50; // default
      if (energyMatch && energyMatch.length > 0) {
        // If range like "0.25-11 Watts", take the higher value
        energyUsage = parseFloat(energyMatch[energyMatch.length - 1]);
      }
      
      // Determine noise level category from specs
      const noiseText = specs["Noise Levels"] || '';
      let noiseLevel = 'moderate';
      if (typeof noiseText === 'string') {
        const noiseLower = noiseText.toLowerCase();
        if (noiseLower.includes('30') || noiseLower.includes('quiet') || noiseLower.includes('whisper')) {
          noiseLevel = 'very-quiet';
        } else if (noiseLower.includes('40') || noiseLower.includes('50')) {
          noiseLevel = 'quiet';
        } else if (noiseLower.includes('60') || noiseLower.includes('70')) {
          noiseLevel = 'moderate';
        }
      }
      
      // Override with important_features quiet_operation if available
      if (features.quiet_operation === true) {
        noiseLevel = 'very-quiet';
      }
      
      // Build features array based on specs and important_features
      const featuresList = [];
      
      // Smart features detection
      const smartFeatures = features.smart_features || {};
      if (smartFeatures.wifi || smartFeatures.app_control) {
        featuresList.push('Smart', 'App Control');
      }
      if (smartFeatures.voice_control) {
        featuresList.push('Voice Control');
      }
      if (smartFeatures.bluetooth) {
        featuresList.push('Bluetooth');
      }
      
      // Smart capabilities
      const smartCapabilities = features.smart_capabilities || {};
      if (smartCapabilities.auto_mode) {
        featuresList.push('Auto Mode');
      }
      if (smartCapabilities.air_quality_sensor) {
        featuresList.push('Air Quality Sensor');
      }
      
      // Filter types
      if (specs["Filter Types"]?.toLowerCase().includes('hepa')) {
        featuresList.push('HEPA');
      }
      if (specs["Filter Types"]?.toLowerCase().includes('carbon') || 
          specs["Filter Types"]?.toLowerCase().includes('activated')) {
        featuresList.push('Carbon Filter');
      }
      
      // Quiet operation
      if (noiseLevel === 'very-quiet' || noiseLevel === 'quiet' || features.quiet_operation) {
        featuresList.push('Quiet');
      }
      
      // Energy Star
      if (specs["Energy Star Certification"]?.toLowerCase() === 'yes' || 
          features.energy_star_certified === true) {
        featuresList.push('Energy Star');
      }
      
      // Maintenance features
      const maintenance = features.maintenance || {};
      if (maintenance.washable_pre_filter) {
        featuresList.push('Washable Pre-Filter');
      }
      if (maintenance.filter_change_indicator) {
        featuresList.push('Filter Change Indicator');
      }
      
      // Build bestFor array using the concerns object - this is now much more accurate!
      const bestFor = ['general'];
      
      if (concerns.allergens_pollen) {
        bestFor.push('allergies');
      }
      if (concerns.dust) {
        bestFor.push('dust');
      }
      if (concerns.smoke) {
        bestFor.push('smoke');
      }
      if (concerns.odors) {
        bestFor.push('odor');
      }
      if (concerns.VOC_chemicals) {
        bestFor.push('voc');
      }
      if (concerns.pet_dander) {
        bestFor.push('pets');
      }
      if (concerns.mold) {
        bestFor.push('mold');
      }
      if (concerns.germs_bacteria_viruses) {
        bestFor.push('germs');
      }
      
      // Add formaldehyde if mentioned
      if (product.product_name?.toLowerCase().includes('formaldehyde') || 
          specs["Filter Types"]?.toLowerCase().includes('formaldehyde')) {
        bestFor.push('formaldehyde');
      }
      
      // Room size categories
      if (coverage >= 1000) {
        bestFor.push('large-space');
      } else if (coverage <= 500) {
        bestFor.push('small-room', 'bedroom');
      } else {
        bestFor.push('medium-room');
      }
      
      // AAFA certification indicates good for allergies/asthma
      if (specs["AAFA Certification"]?.toLowerCase() === 'yes') {
        bestFor.push('asthma');
      }
      
      // Budget category
      if (price < 200) {
        bestFor.push('budget');
      }
      
      // Estimate filter cost and lifespan
      const filterCost = price > 800 ? 100 : price > 500 ? 70 : price > 300 ? 60 : 50;
      const filterLifespan = maintenance.estimated_filter_life_months 
        ? `${maintenance.estimated_filter_life_months} months`
        : "6-8 months";
      
      // Create product object for the app
      const productObj = {
        name: product.product_name || "Unknown Product",
        brand: product.brand || manufacturerName,
        manufacturer: manufacturerName,
        price: price,
        roomSize: coverage,
        cadr: cadr,
        features: featuresList,
        bestFor: [...new Set(bestFor)], // Remove duplicates
        noiseLevel: noiseLevel,
        filterCost: filterCost,
        filterLifespan: filterLifespan,
        energyUsage: energyUsage,
        warranty: specs["Warranty"] || "1 year",
        dimensions: specs["Size"] || "N/A",
        weight: specs["Weight"] || "N/A",
        airChangesPerHour: coverage > 0 ? Math.ceil(cadr / (coverage / 60)) : 0,
        certifications: [
          specs["Energy Star Certification"] === "yes" ? "Energy Star" : null,
          specs["AAFA Certification"] === "yes" ? "AAFA" : null,
          specs["Additional Certifications"]
        ].filter(Boolean),
        rating: 4.5, // Default rating
        reviews: 100, // Default reviews
        availability: "In Stock",
        shippingTime: "2-3 days",
        description: `${manufacturerName} ${product.product_name} with ${specs["Filter Types"] || 'HEPA filtration'}`,
        detailedReview: `The ${product.product_name} from ${manufacturerName} covers up to ${coverage} sq ft with a CADR of ${cadr} CFM. ${smartFeatures.app_control || smartFeatures.wifi ? 'Features smart controls for easy management.' : 'Simple, reliable operation.'}`,
        amazonLink: specs["Amazon Link"] || "",
        filterTypes: specs["Filter Types"] || "",
        fanSpeeds: specs["Fan Speeds"] || "",
        hsaFsaEligible: specs["HSA/FSA Eligibility"]?.toLowerCase() === 'yes',
        // Store original data for advanced features
        _rawConcerns: concerns,
        _rawFeatures: features
      };
      
      // Only add products with valid data
      if (productObj.price > 0 && productObj.roomSize > 0) {
        products.push(productObj);
      }
    });
  });
  
  return products;
};

// Get the transformed product database
export const getProductDatabase = () => {
  return transformProducts();
};

// Helper to get unique manufacturers
export const getManufacturers = () => {
  return Object.keys(productsData).map(key => productsData[key].manufacturer || key);
};

// Helper to get products by manufacturer
export const getProductsByManufacturer = (manufacturer) => {
  const allProducts = transformProducts();
  return allProducts.filter(p => p.manufacturer === manufacturer);
};

// Helper to get room size range from database
export const getRoomSizeRange = () => {
  const allProducts = transformProducts();
  const roomSizes = allProducts.map(p => p.roomSize).filter(size => size > 0);
  
  return {
    min: Math.min(...roomSizes),
    max: Math.max(...roomSizes),
    average: Math.round(roomSizes.reduce((a, b) => a + b, 0) / roomSizes.length)
  };
};

// Helper to get price range from database
export const getPriceRange = () => {
  const allProducts = transformProducts();
  const prices = allProducts.map(p => p.price).filter(price => price > 0);
  
  return {
    min: Math.min(...prices),
    max: Math.max(...prices),
    average: Math.round(prices.reduce((a, b) => a + b, 0) / prices.length)
  };
};